import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, KeyRound } from 'lucide-react';
import { Badge, Button, useToast } from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonKeys, daemonReads, daemonWrites } from './daemon-queries.js';
import {
  ACTION_LABEL,
  RISK_VARIANTS,
  RunEnvelope,
  STATUS_VARIANTS,
  asKey,
  asRecord,
  asText,
  formatCommand,
  unwrapListResponse,
} from './run-audit-shared.js';

export const ExecutionPolicyPanel: React.FC<{ daemonId: number; runId: number }> = ({ daemonId, runId }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, policy: mutatePolicy } = useDaemonScope();

  const envelopesQuery = useQuery(daemonReads(scope).runEnvelopes(daemonId, runId));

  const grantMutation = useMutation({
    ...daemonWrites(scope).grantEnvelope(),
    onSuccess: () => {
      notify('Permission granted');
      queryClient.invalidateQueries({ queryKey: daemonKeys(scope).envelopes(daemonId, runId) });
    },
    onError: () => notify('Could not grant the permission', 'danger'),
  });
  const grant = (envelopeId: number, grantMode: 'once' | 'always') =>
    grantMutation.mutate({
      path: { id: String(daemonId), runId: String(runId), envelopeId: String(envelopeId) },
      body: {
        grantMode,
        reason: grantMode === 'once' ? 'Allowed once from daemon run audit' : 'Allowed for this execution scope',
      },
    });

  const envelopes = unwrapListResponse<RunEnvelope>(envelopesQuery.data);
  const deniedCount = envelopes.filter((envelope) => envelope.status === 'denied').length;

  if (envelopesQuery.isLoading) {
    return <div className="text-xs text-muted">Loading execution policy...</div>;
  }

  if (envelopesQuery.isError) {
    return <div className="text-xs text-danger">Could not load execution policy.</div>;
  }

  if (envelopes.length === 0) {
    return null;
  }

  return (
    <div className="rounded-sm border bg-surface p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-body">
          <ShieldCheck size={14} className="text-accent-text" />
          Execution Policy
          <Badge variant={deniedCount > 0 ? 'danger' : 'neutral'} size="sm">
            {deniedCount > 0 ? `${deniedCount} blocked` : `${envelopes.length} checked`}
          </Badge>
        </div>
        {deniedCount > 0 && (
          <div className="text-xs text-muted">Grant a scoped permission, then run the daemon again.</div>
        )}
      </div>

      <div className="space-y-2">
        {envelopes.map((envelope) => {
          const command = asRecord(envelope.command);
          const policy = asRecord(envelope.policy);
          const scopeDetails = asRecord(policy.scope);
          const isDenied = envelope.status === 'denied';
          return (
            <div key={envelope.id} className="rounded-sm border bg-surface-alt p-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-body">
                      {asText(envelope.commandName) || `Envelope ${asText(envelope.id)}`}
                    </span>
                    {envelope.action ? (
                      <Badge variant="neutral" size="sm">
                        {ACTION_LABEL[asText(envelope.action)] ?? asText(envelope.action)}
                      </Badge>
                    ) : null}
                    <Badge variant={STATUS_VARIANTS[asKey(envelope.status)] ?? 'neutral'} size="sm">
                      {asText(envelope.status)}
                    </Badge>
                    <Badge variant={RISK_VARIANTS[asKey(envelope.risk)] ?? 'neutral'} size="sm">
                      {asText(envelope.risk)} risk
                    </Badge>
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {String(scopeDetails.resourceId ?? envelope.resourceId ?? 'container')}
                    {' / '}
                    network={String(scopeDetails.network ?? command.network ?? 'none')}
                    {' / '}
                    readOnly={String(scopeDetails.readOnly ?? 'unknown')}
                  </div>
                  <div className="font-mono text-xs text-muted break-all mt-1">{formatCommand(command.command)}</div>
                </div>

                {isDenied && (
                  <Can policy={mutatePolicy}>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={grantMutation.isPending}
                        onClick={() => grant(envelope.id, 'once')}
                      >
                        <KeyRound size={14} />
                        Allow once
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={grantMutation.isPending}
                        onClick={() => grant(envelope.id, 'always')}
                      >
                        <KeyRound size={14} />
                        Allow always
                      </Button>
                    </div>
                  </Can>
                )}
              </div>
              {envelope.decisionReason ? (
                <div className="text-xs text-muted mt-2">{asText(envelope.decisionReason)}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
